import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { faFacebookF, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faShare } from '@fortawesome/pro-solid-svg-icons';
import { format, isFuture, parseJSON } from 'date-fns';
import { BlogArticle } from 'src/app/blog-article.model';
import { MetaSetterService } from 'src/app/general/meta-setter.service';
import { LocalisationService } from 'src/app/localisation.service';
import { SPORTS } from 'src/app/sports';
import { environment } from 'src/environments/environment';
import { BlogService } from '../../blog.service';

@Component({
	selector: 'app-article-page',
	templateUrl: './article-page.component.html',
	styleUrls: ['./article-page.component.scss'],
})
export class ArticlePageComponent implements OnInit {
	slug: string;
	article: BlogArticle;
	articleLoading: boolean = true;
	articleError: boolean = false;
	trustedContent: SafeHtml;
	formattedDate: string = "";
	formattedUpdatedDate: string = "";
	isPreview = false;
	revisionID = "";

	shareButtonAvailable = false;

	environment = environment;

	faFacebookF = faFacebookF;
	faTwitter = faTwitter;
	faEnvelope = faEnvelope;
	faShare = faShare;

	constructor(
		private blogService: BlogService,
		private route: ActivatedRoute,
		private metaSetter: MetaSetterService,
		private sanitiser: DomSanitizer,
		private localisationService: LocalisationService,
	) {
		this.slug = this.route.snapshot.paramMap.get("slug");
		if (this.route.snapshot.url.some(u => u.path === "preview")) {
			this.isPreview = true;
			this.revisionID = this.route.snapshot.queryParamMap.get("revision");
		}

		if (typeof window !== "undefined" && navigator?.share) {
			this.shareButtonAvailable = true;
		}
	}

	ngOnInit(): void {
		this.loadArticleData();

		// check for change in article slug
		this.route.params.subscribe((data) => {
			if (data.slug && this.slug !== data.slug) {
				this.slug = data.slug;

				this.loadArticleData();

			}
		});
	}

	openShareDialog(): void {
		if (navigator?.share) {
			navigator.share({
				title: this.article.title,
				url: `${environment.canonicalDomain}/news/${this.slug.toLowerCase()}`
			  }).then(() => {
				console.log('Thanks for sharing!');
			  })
		}
	}

	loadArticleData(): void {
		this.articleLoading = true;
		this.articleError = false;

		this.blogService.getArticle(this.slug, this.revisionID)
			.subscribe(
				(article: BlogArticle) => this.postArticleFetch(article),
				_ => {
					this.articleError = true;
				}
			);
	}

	postArticleFetch(article: BlogArticle): void {
		this.article = article;

		const publishedDate = this.article.published_date ? parseJSON(this.article.published_date) : null;
		const unpublishedDate = this.article.unpublished_at ? parseJSON(this.article.unpublished_at) : null;

		// if article has no published date, has an unpublished date, or published date is in the future,
		// don't allow access to article unless it's an internal preview page
		if (!this.isPreview && (!publishedDate || unpublishedDate || isFuture(publishedDate))) {
			console.warn("Attempted to access unpublished article on main article page")
			this.articleError = true;
		} else {
			this.trustedContent = this.sanitiser.bypassSecurityTrustHtml(article.content_description);
			const publishedDateFormat: Intl.DateTimeFormatOptions = {
				timeZone: "America/New_York",
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: "numeric",
				minute: "numeric",
				timeZoneName: "shortGeneric"
			}
			if (this.article.last_updated && this.article.published_date !== this.article.last_updated) {

				// this.formattedDate = `${format(parseJSON(this.article.published_date), "MMM d, yyyy")},
				// 	${format(parseJSON(this.article.published_date), "h:mma")}`;
				this.formattedDate = format(parseJSON(this.article.published_date), "MMM d, yyyy, h:mma");
				this.formattedUpdatedDate = format(parseJSON(this.article.last_updated), "MMM d, yyyy, h:mma");
			} else if (this.article?.published_date) {
				this.formattedDate = format(parseJSON(this.article.published_date), "MMM d, yyyy, h:mma");
				this.formattedUpdatedDate = "";
			} else {
				this.formattedDate = "Unpublished";
				this.formattedUpdatedDate = "";
			}

			// const unifiedSlug = this.article.slug + "-" + this.article.id;

			// if (this.slug !== unifiedSlug) {
			// 	this.slug = unifiedSlug;
			// }
			this.metaSetter.setMeta(
				{
					title: this.article.title,
					seoTitle: (this.article.social_summary_title || this.article.title),
					description: this.article.social_summary_description,
					metaImageURL: this.article.socialThumbnail ?
						this.article.socialThumbnail.url.replace(" ", "%20").replace("https", "http")
						: this.article.thumbnail.url.replace(" ", "%20").replace("https", "http"),
					ampURL: environment.ampDomain ? (environment.ampDomain + `/${this.slug.toLowerCase()}`) : undefined,
					canonicalURL: environment.canonicalDomain + `/news/${this.slug.toLowerCase()}`,
					structuredData: {
						"@context": "https://schema.org",
						"@type": "NewsArticle",
						"headline": this.article.title,
						"image": [
							this.article.socialThumbnail ?
								this.article.socialThumbnail.url.replace(" ", "%20").replace("https", "http")
								: this.article.thumbnail.url.replace(" ", "%20").replace("https", "http")
						],
						"datePublished": this.article.created_at,
						"dateModified": (this.article.published_date && this.article.published_date !== this.article.created_at) ?
							this.article.published_date : null,
						"author": [{
							"@type": "Person",
							"name": this.article.authors?.[0] ? (this.article.authors[0].first_name + " " + this.article.authors[0].last_name)
								: this.article.author,
							"url": this.article.authors?.[0]
								? `${environment.canonicalDomain}/news/author/${this.article.authors[0].slug.toLowerCase()}`
								: undefined,
						}],
						"publisher": {
							"name": environment.siteTitle,     
							"url": environment.canonicalDomain 
						}
					}
				}, 
				true
			)

			if (environment.siteTitle === "Dimers" && typeof window !== "undefined" && this.article.tags?.[0]?.slug.toUpperCase() === "MLB") {
				// Initialise MLB Desktop Banner
				(function (w, d, s, o, f, id) {
					w[o] = w[o] || function () {
						(w[o].q = w[o].q || []).push(arguments)
					};
					let js = d.getElementById(o) as any;
					if (!js) {
						js = d.createElement(s);
						let fjs = d.getElementById(id) as any;
						js.id = o;
						js.src = f;
						js.async = 1;
						fjs.parentNode.insertBefore(js, fjs);
					}
					let sc = d.getElementById(id);
					let el = d.createElement("div");
					el.id = id + '-el';
					sc.parentNode.replaceChild(el, sc);
				}(window, document, 'script', 'dkWidget', 'https://api.draftkings.com/widgets/static/v1/dkwidget.js', 'a6285c83-b252-46c8-8845-dc8f0f316a83'));
				(window as any).dkWidget('init', {
					"useIFrame": true,
					"publisher": "Dimers",
					"sportName": "Baseball",
					"competitionName": "MLB",
					"offerId": "19737",
					"allowFallback": true,
					"size": "728x90",
					"callToActionLink": "https://dksb.sng.link/As9kz/jmwk?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D509524528%26wpcid%3D208197%26wpcn%3DWidgets%26wpcrid%3DadUnits%26wpsn%3DDimers&pcid=208197&psn=Dimers&pcn=Widgets&pscn=firstEmbedCode&pcrid=adUnits",
					"useDynamicCTAParameters": false,
					"analyticsId": "Cortana-208197",
					"id": "a6285c83-b252-46c8-8845-dc8f0f316a83-el"
				}, "https://api.draftkings.com/widgets");

				(window as any).dkWidget('init', {
					"useIFrame": true,
					"publisher": "Dimers",
					"sportName": "Baseball",
					"competitionName": "MLB",
					"offerId": "19737",
					"allowFallback": true,
					"size": "300x50",
					"callToActionLink": "https://dksb.sng.link/As9kz/jmwk?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D509524528%26wpcid%3D208197%26wpcn%3DWidgets%26wpcrid%3DadUnits%26wpsn%3DDimers&pcid=208197&psn=Dimers&pcn=Widgets&pscn=firstEmbedCode&pcrid=adUnits",
					"useDynamicCTAParameters": false,
					"analyticsId": "Cortana-208197",
					"id": "e6979b4e-f640-4bf4-b348-c08bf3d71986"
				}, "https://api.draftkings.com/widgets");
			}


			if (environment.siteTitle === "Dimers" && typeof window !== "undefined" && this.article.tags?.[0]?.slug.toUpperCase() === "CFB") {
				// Initialise MLB Desktop Banner
				(function (w, d, s, o, f, id) {
					w[o] = w[o] || function () {
						(w[o].q = w[o].q || []).push(arguments)
					};
					let js = d.getElementById(o) as any;
					if (!js) {
						js = d.createElement(s);
						let fjs = d.getElementById(id) as any;
						js.id = o;
						js.src = f;
						js.async = 1;
						fjs.parentNode.insertBefore(js, fjs);
					}
					let sc = d.getElementById(id);
					let el = d.createElement("div");
					el.id = id + '-el';
					sc.parentNode.replaceChild(el, sc);
				}(window, document, 'script', 'dkWidget', 'https://api.draftkings.com/widgets/static/v1/dkwidget.js', 'a6285c83-b252-46c8-8845-dc8f0f316a83'));
				(window as any).dkWidget('init', {
					"useIFrame": true,
					"publisher": "Dimers",
					"sportName": "Football",
					"competitionName": "CFB",
					"offerId": "19737",
					"allowFallback": true,
					"size": "728x90",
					"callToActionLink": "https://dksb.sng.link/As9kz/jmwk?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D509524528%26wpcid%3D208197%26wpcn%3DWidgets%26wpcrid%3DadUnits%26wpsn%3DDimers&pcid=208197&psn=Dimers&pcn=Widgets&pscn=firstEmbedCode&pcrid=adUnits",
					"useDynamicCTAParameters": false,
					"analyticsId": "Cortana-208197",
					"id": "a6285c83-b252-46c8-8845-dc8f0f316a83-el"
				}, "https://api.draftkings.com/widgets");

				(window as any).dkWidget('init', {
					"useIFrame": true,
					"publisher": "Dimers",
					"sportName": "Football",
					"competitionName": "CFB",
					"offerId": "19737",
					"allowFallback": true,
					"size": "300x50",
					"callToActionLink": "https://dksb.sng.link/As9kz/jmwk?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D509524528%26wpcid%3D208197%26wpcn%3DWidgets%26wpcrid%3DadUnits%26wpsn%3DDimers&pcid=208197&psn=Dimers&pcn=Widgets&pscn=firstEmbedCode&pcrid=adUnits",
					"useDynamicCTAParameters": false,
					"analyticsId": "Cortana-208197",
					"id": "e6979b4e-f640-4bf4-b348-c08bf3d71986"
				}, "https://api.draftkings.com/widgets");
			}

			if (typeof window !== "undefined" && this.article.tags?.[0]?.slug.toUpperCase() === "CFB") {
				// Initialise MLB Desktop Banner
				(function (w, d, s, o, f, id) {
					w[o] = w[o] || function () {
						(w[o].q = w[o].q || []).push(arguments)
					};
					let js = d.getElementById(o) as any;
					if (!js) {
						js = d.createElement(s);
						let fjs = d.getElementById(id) as any;
						js.id = o;
						js.src = f;
						js.async = 1;
						fjs.parentNode.insertBefore(js, fjs);
					}
					let sc = d.getElementById(id);
					let el = d.createElement("div");
					el.id = id + '-el';
					sc.parentNode.replaceChild(el, sc);
				}(window, document, 'script', 'dkWidget', 'https://api.draftkings.com/widgets/static/v1/dkwidget.js', 'a6285c83-b252-46c8-8845-dc8f0f316a83'));
				(window as any).dkWidget('init', {
					"useIFrame": true,
					"publisher": "Dimers",
					"sportName": "Football",
					"competitionName": "CFB",
					"offerId": "19737",
					"allowFallback": true,
					"size": "728x90",
					"callToActionLink": "https://dksb.sng.link/As9kz/jmwk?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D509524528%26wpcid%3D208197%26wpcn%3DWidgets%26wpcrid%3DadUnits%26wpsn%3DDimers&pcid=208197&psn=Dimers&pcn=Widgets&pscn=firstEmbedCode&pcrid=adUnits",
					"useDynamicCTAParameters": false,
					"analyticsId": "Cortana-208197",
					"id": "a6285c83-b252-46c8-8845-dc8f0f316a83-el"
				}, "https://api.draftkings.com/widgets");

				(window as any).dkWidget('init', {
					"useIFrame": true,
					"publisher": "Dimers",
					"sportName": "Football",
					"competitionName": "CFB",
					"offerId": "19737",
					"allowFallback": true,
					"size": "300x50",
					"callToActionLink": "https://dksb.sng.link/As9kz/jmwk?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D509524528%26wpcid%3D208197%26wpcn%3DWidgets%26wpcrid%3DadUnits%26wpsn%3DDimers&pcid=208197&psn=Dimers&pcn=Widgets&pscn=firstEmbedCode&pcrid=adUnits",
					"useDynamicCTAParameters": false,
					"analyticsId": "Cortana-208197",
					"id": "e6979b4e-f640-4bf4-b348-c08bf3d71986"
				}, "https://api.draftkings.com/widgets");
			}
		}

		this.articleLoading = false;
	}

	inSeasonSport(category: string) {
		return this.localisationService.sportPrioritySort(SPORTS).find(s => s.code.toUpperCase() === category.toUpperCase() && s.active)
	}

}
